/**
 * Primary key icon at the **trailing** edge of the column header (same placement rules as
 * {@link AIColumnHeaderIconExtension}). Primary columns are never AI-enabled, so no overlap
 * handling with the AI icon is needed here.
 * Same **VpnKey** glyph as `@mui/icons-material` / CRM mapping, `themePrimary` fill.
 * Z-index below AI so AI columns elsewhere stay visually on top.
 */
import type { IScale } from '@univerjs/core';
import { SheetExtension, SHEET_EXTENSION_TYPE } from '@univerjs/engine-render';
import type { SpreadsheetSkeleton } from '@univerjs/engine-render';
import type { UniverRenderingContext } from '@univerjs/engine-render';
const PRIMARY_ICON_SIZE = 13;
/** Match {@link AIColumnHeaderIconExtension} trailing padding / text gap. */
const ICON_PADDING_RIGHT = 4;
const GAP_AFTER_TEXT = 12;
const HEADER_TEXT_PADDING_LEFT = 6;
const HEADER_FONT = '12px "Helvetica Neue", Helvetica, Arial, sans-serif';

/** Matches {@link AIColumnHeaderIconExtension} / CRM mapping when `themePrimary` is passed. */
const DEFAULT_ICON_COLOR = '#49BAFD';

/**
 * Exact `path` from `@mui/icons-material` **VpnKey** (same asset as CRM sync primary-key column).
 */
function getPrimaryIconSvgDataUrl(fillColor: string): string {
  const fill = fillColor.replace(/"/g, '&quot;');
  const d =
    'M12.65 10C11.83 7.67 9.61 6 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6c2.61 0 4.83-1.67 5.65-4H17v4h4v-4h2v-4zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${fill}"><path d="${d}"/></svg>`;
  return 'data:image/svg+xml,' + encodeURIComponent(svg);
}

const imageCacheByColor: Record<string, HTMLImageElement> = {};
let loadCallback: (() => void) | null = null;

function getIconImage(
  iconColor: string,
  onLoaded?: () => void
): HTMLImageElement {
  const key = iconColor || DEFAULT_ICON_COLOR;
  const cached = imageCacheByColor[key];
  if (cached?.complete && cached.naturalWidth > 0) {
    return cached;
  }
  const img = new Image();
  loadCallback = onLoaded ?? null;
  img.onload = () => {
    loadCallback?.();
    loadCallback = null;
  };
  img.src = getPrimaryIconSvgDataUrl(key);
  imageCacheByColor[key] = img;
  return img;
}

export type GetPrimaryColumnIndices = () => Set<number>;
export type GetColumnDisplayName = (colIndex: number) => string;

export function createPrimaryColumnHeaderIconExtension(
  getPrimaryColumnIndices: GetPrimaryColumnIndices,
  onIconLoaded?: () => void,
  iconColor: string = DEFAULT_ICON_COLOR,
  getColumnDisplayName?: GetColumnDisplayName
): SheetExtension {
  const fillColor = iconColor || DEFAULT_ICON_COLOR;
  getIconImage(fillColor, onIconLoaded);

  class PrimaryColumnHeaderIconExtension extends SheetExtension {
    override uKey = 'PrimaryColumnHeaderIconExtension';
    override type = SHEET_EXTENSION_TYPE.GRID;
    /** Below AI header icons (100); above default header fill (10). */
    protected override Z_INDEX = 90;

    override draw(
      ctx: UniverRenderingContext,
      parentScale: IScale,
      skeleton: SpreadsheetSkeleton
    ): void {
      const tx = this.translateX ?? 0;
      const ty = this.translateY ?? 0;

      const primaryCols = getPrimaryColumnIndices();
      if (primaryCols.size === 0) return;

      const sk = skeleton as unknown as {
        getColumnCount?: () => number;
        columnHeaderHeight?: number;
        columnWidthAccumulation?: number[];
        rowColumnSegment?: { startColumn: number; endColumn: number };
      };
      const columnCount =
        sk.getColumnCount?.() ?? sk.columnWidthAccumulation?.length ?? 0;
      if (columnCount <= 0) return;

      const scaleX = parentScale?.scaleX ?? 1;
      const scaleY = parentScale?.scaleY ?? 1;
      const columnHeaderHeight = sk.columnHeaderHeight ?? 24;
      const headerH = columnHeaderHeight * scaleY;
      const iconY = ty + (headerH - PRIMARY_ICON_SIZE) / 2;

      const columnWidthAccumulation = sk.columnWidthAccumulation;
      if (
        !Array.isArray(columnWidthAccumulation) ||
        columnWidthAccumulation.length === 0
      )
        return;

      const segment = sk.rowColumnSegment;
      const startCol =
        segment && segment.startColumn >= 0 && segment.endColumn >= 0
          ? segment.startColumn
          : 0;
      const endCol =
        segment && segment.startColumn >= 0 && segment.endColumn >= 0
          ? Math.min(segment.endColumn + 1, columnCount)
          : columnCount;

      const img = getIconImage(fillColor);

      ctx.save();
      ctx.font = HEADER_FONT;

      for (let col = startCol; col < endCol; col++) {
        if (!primaryCols.has(col)) continue;

        const startX = col === 0 ? 0 : (columnWidthAccumulation[col - 1] ?? 0);
        const endX = columnWidthAccumulation[col] ?? startX;
        const cellW = (endX - startX) * scaleX;

        const cellLeft = tx + startX * scaleX;
        const rightEdge = tx + endX * scaleX;

        const minCellW = PRIMARY_ICON_SIZE + ICON_PADDING_RIGHT;
        if (cellW < minCellW) continue;

        const preferredX = rightEdge - PRIMARY_ICON_SIZE - ICON_PADDING_RIGHT;
        const maxX = preferredX;

        let x = preferredX;
        if (getColumnDisplayName) {
          const label = getColumnDisplayName(col) ?? '';
          const textWidth = ctx.measureText(label).width;
          const minX =
            cellLeft + HEADER_TEXT_PADDING_LEFT + textWidth + GAP_AFTER_TEXT;
          x = Math.max(minX, preferredX);
        }
        x = Math.min(x, maxX);
        if (x < cellLeft) continue;

        if (img.complete && img.naturalWidth > 0) {
          ctx.drawImage(img, x, iconY, PRIMARY_ICON_SIZE, PRIMARY_ICON_SIZE);
        } else {
          ctx.fillStyle = fillColor + '99';
          ctx.fillRect(x, iconY, PRIMARY_ICON_SIZE, PRIMARY_ICON_SIZE);
        }
      }

      ctx.restore();
    }
  }

  return new PrimaryColumnHeaderIconExtension();
}
